import React, { useEffect } from 'react'

import {Column, useSortBy, useTable, PluginHook } from 'react-table'
import Table, { Props as TableProps } from '../../atoms/table/Table'
import Icon from '../../atoms/icon/Icon'
import './Sortable-table.scss'
import { useHotkeys } from 'react-hotkeys-hook'
import { rowSelectPlugin } from './plugins'

interface Props<T extends object> extends TableProps{
  columns: Array<Column<T>>
  data: Array<any>
  /**
   * This is the callback to fetch selected rows and also enable row select plugin
   */
  selectedRowsCallback?: (selectedRows: T[]) => void
  onRowClick?: (event: { stopPropagation: () => void, preventDefault: () => void }, row: { original: T }) => any
}

function SortableTable<T extends object> ({ columns, data, selectedRowsCallback, onRowClick, ...rest } : Props<T>) {
  const plugins: PluginHook<T>[] = [useSortBy]
  if (selectedRowsCallback) {
    plugins.push(...rowSelectPlugin)
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    toggleAllRowsSelected,
  } = useTable<T>({
    columns,
    data,
  },
  ...plugins
  )

  useEffect(() => {
    selectedRowsCallback && selectedRowsCallback(selectedFlatRows.map(row => row.original))
  }, [selectedFlatRows, selectedRowsCallback])

  useHotkeys('ctrl+a, command+a', (event) => {
    event.preventDefault()
    toggleAllRowsSelected()
  })

  return (
    <Table {...getTableProps()} {...rest} className="sortable-table">
      <thead>
        {headerGroups.map((headerGroup, i) => (
          <tr {...headerGroup.getHeaderGroupProps()} key={i}>
            {headerGroup.headers.map(column => (
              <th {...column.getHeaderProps(column.getSortByToggleProps())} key={column.id}>
                {column.render('Header')}
                <span>
                  {column.isSorted
                    ? column.isSortedDesc
                      ? <Icon type="arrow" className="arrowDesc" />
                      : <Icon type="arrow" className="arrowAsc" />
                    : ''}
                </span>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row: any) => {
          prepareRow(row)
          return (
            <tr {...row.getRowProps()} data-test={row.original.id}>
              {row.cells.map((cell: { getCellProps: () => JSX.IntrinsicAttributes & React.ClassAttributes<HTMLTableDataCellElement> & React.TdHTMLAttributes<HTMLTableDataCellElement>, render: (arg0: string) => React.ReactNode }, index: number) => {
                return <td {...cell.getCellProps()} onClick={(event: { stopPropagation: () => void, preventDefault: () => void }) => onRowClick && index ? onRowClick(event, row) : null}>{cell.render('Cell')}</td>
              })}
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}

export default SortableTable
